import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { ROTATE_HANDLE_CENTER_OFFSET } from '@/lib/editor/constants/rotate-handle-center-offset'
import {
  CROP_RESIZE_HANDLE_SCREEN_RADIUS,
  MOVE_BADGE_DEFAULT_SCREEN_SIZE,
  ROTATE_BADGE_CORNER_CLEARANCE_PX,
  ROTATE_BADGE_SIZE_RATIO,
  ROTATE_BASE_ANGLE_DEG,
  ROTATE_LINE_LENGTH_RATIO,
} from '@/lib/editor/constants/crop-overlay-constants'

/** Rotate badge's own radius (screen px) at the badge's largest/default size. */
const ROTATE_BADGE_MAX_RADIUS_PX = (MOVE_BADGE_DEFAULT_SCREEN_SIZE * ROTATE_BADGE_SIZE_RATIO) / 2

/**
 * Worst-case screen-px reach of `CropOverlay`'s photo-rotate dial (badge + connecting line) from
 * the shape's center. Mirrors `CropOverlay`'s own worst-case geometry, at the badge's
 * largest/default size:
 *
 * - `defaultRotateCenterDistMax`: the dial's normal (badge-size-based) distance from center —
 *   `badgeR` + `lineLengthDoc` + `rotateR`.
 * - `cornerClearanceMax`: how much further `CropOverlay` can push the dial out, ONLY when a
 *   corner resize handle would otherwise land within this clearance of the dial's normal
 *   position — see the `cornerReachDoc`/`wouldOverlapCorner` block in `crop-overlay/helpers/badge-layout.ts`. The
 *   pushed-out distance can approach (never quite reach) `defaultRotateCenterDistMax +
 *   2 * cornerClearanceMax`, so that bounds the dial CENTER; add `rotateR` once more for its own
 *   radius to reach the badge's far edge.
 *
 * The badge shrinks for small shapes, but this bounds utility doesn't have the shape's on-screen
 * VISUAL size handy (only the geometric rect), so it uses the largest possible reach — erring
 * generous rather than risking under-covering a large shape's full-size dial.
 */
const PHOTO_ROTATE_DIAL_MAX_REACH_PX = (() => {
  const defaultRotateCenterDistMax =
    MOVE_BADGE_DEFAULT_SCREEN_SIZE / 2 +
    MOVE_BADGE_DEFAULT_SCREEN_SIZE * ROTATE_LINE_LENGTH_RATIO +
    ROTATE_BADGE_MAX_RADIUS_PX
  const cornerClearanceMax =
    CROP_RESIZE_HANDLE_SCREEN_RADIUS + ROTATE_BADGE_CORNER_CLEARANCE_PX + ROTATE_BADGE_MAX_RADIUS_PX

  return defaultRotateCenterDistMax + 2 * cornerClearanceMax + ROTATE_BADGE_MAX_RADIUS_PX
})()

/** Shape bounds projected to screen/viewport coordinates (axis-aligned, ignores rotation). */
export function getShapeScreenRect(
  shape: Pick<ShapeData, 'x' | 'y' | 'width' | 'height'>,
  scale: number,
  canvasRect: DOMRect
): { left: number; top: number; right: number; bottom: number } {
  const left = canvasRect.left + shape.x * scale
  const top = canvasRect.top + shape.y * scale

  return { left, top, right: left + shape.width * scale, bottom: top + shape.height * scale }
}

/**
 * Screen-space bounding rect of everything crop mode's overlay (`CropOverlay`) draws around the
 * shape — the rotated frame corners (resize handles), the shape-rotate handle (which sits
 * `ROTATE_HANDLE_CENTER_OFFSET` screen px beyond the frame's un-rotated top edge and swings around
 * the shape as `shape.rotation` changes), and the photo-content rotate dial (which swings around
 * the shape's center as `shape.photoFill.rotation` changes, independent of `shape.rotation` — it's
 * rendered outside the shape's own rotation transform). Used by `CropToolbar`/`CropZoomSlider` so
 * their floating placement never lands on top of a resize handle or either rotate control, at any
 * angle (WMPCM "zoom toolbar overlaps the rotate handle/corners once the shape is rotated", and
 * "toolbar overlaps the photo rotate dial once it's spun toward it").
 *
 * Falls back to the exact `getShapeScreenRect` when both rotations are zero — the GAP constants in
 * `CropToolbar`/`CropZoomSlider` are already tuned to clear the shape-rotate handle's un-rotated
 * (always above the shape) position against that rect, so this only needs to grow the rect once
 * either rotation actually moves one of the handles.
 *
 * Uses the shape's GEOMETRIC rect, not its tight VISUAL rect (see `shape-coordinates.ts`) — for an
 * inset shape (star/triangle/custom path) the geometric rect is a superset of the visual one, so
 * this errs slightly generous rather than under-covering.
 */
export function getShapeChromeScreenRect(
  shape: Pick<ShapeData, 'x' | 'y' | 'width' | 'height' | 'rotation'> & {
    photoFill?: { rotation?: number }
  },
  scale: number,
  canvasRect: DOMRect
): { left: number; top: number; right: number; bottom: number } {
  const rotation = shape.rotation ?? 0
  const photoRotation = shape.photoFill?.rotation ?? 0

  if (!rotation && !photoRotation) return getShapeScreenRect(shape, scale, canvasRect)

  const { x, y, width, height } = shape
  const cx = x + width / 2
  const cy = y + height / 2
  const stemLength = ROTATE_HANDLE_CENTER_OFFSET / scale

  // The 4 frame corners (resize handles) plus the shape-rotate handle, which sits `stemLength`
  // above the frame's un-rotated top-center — same points `CropOverlay` positions its handles at,
  // rotated the same way (`rotate(shape.rotation, cx, cy)`).
  const points = [
    { x, y },
    { x: x + width, y },
    { x: x + width, y: y + height },
    { x, y: y + height },
    { x: cx, y: y - stemLength },
  ]

  const rad = (rotation * Math.PI) / 180
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)

  let minX = Infinity
  let maxX = -Infinity
  let minY = Infinity
  let maxY = -Infinity

  for (const p of points) {
    const dx = p.x - cx
    const dy = p.y - cy
    const rx = cx + dx * cos - dy * sin
    const ry = cy + dx * sin + dy * cos

    minX = Math.min(minX, rx)
    maxX = Math.max(maxX, rx)
    minY = Math.min(minY, ry)
    maxY = Math.max(maxY, ry)
  }

  if (photoRotation) {
    // NOT part of the `rotate(shape.rotation, ...)` group above — the photo-rotate dial is drawn
    // screen-upright relative to the shape's own rotation and orbits the center purely based on
    // `ROTATE_BASE_ANGLE_DEG + photoRotation` (see `CropOverlay`'s `dirX`/`dirY`, which reduce to
    // exactly this angle via the cosine/sine angle-sum identity).
    const dialAngleRad = ((ROTATE_BASE_ANGLE_DEG + photoRotation) * Math.PI) / 180
    const reach = PHOTO_ROTATE_DIAL_MAX_REACH_PX / scale
    const dialX = cx + Math.cos(dialAngleRad) * reach
    const dialY = cy + Math.sin(dialAngleRad) * reach

    minX = Math.min(minX, dialX)
    maxX = Math.max(maxX, dialX)
    minY = Math.min(minY, dialY)
    maxY = Math.max(maxY, dialY)
  }

  return {
    left: canvasRect.left + minX * scale,
    top: canvasRect.top + minY * scale,
    right: canvasRect.left + maxX * scale,
    bottom: canvasRect.top + maxY * scale,
  }
}
